import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CommitteeMembershipSelectControl } from '@/module/Nomen/CommitteeMembership/CommitteeMembershipSelectControl';
import { CommitteeTypeSelectControl } from '@/module/Nomen/CommitteeType/CommitteeTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { CommitteeSchema } from './model/CommitteeSchema';

interface CommitteeFormProps extends FormSubmit<CommitteeSchema> {
  title: string;
}

export function CommitteeForm(props: CommitteeFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<CommitteeSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <Group>
              <TextInputControl<CommitteeSchema>
                name="title"
                label={t('committees.form.fields.title')}
                isSubmitting={isSubmitting}
              />
            </Group>

            <Group align="flex-end">
              <NumberInputControl<CommitteeSchema>
                name="startYear"
                label={t('committees.form.fields.startYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.5}
              />
              <NumberInputControl<CommitteeSchema>
                name="endYear"
                label={t('committees.form.fields.endYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.5}
              />
            </Group>

            <Group>
              <CommitteeTypeSelectControl<CommitteeSchema>
                withAsterisk
                name="type"
                label={t('committees.form.fields.committeeType')}
                isSubmitting={isSubmitting}
              />
            </Group>
          </Content>

          <Content type="full">
            <AuthorListControl<CommitteeSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <CommitteeMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
